import { useEffect, useMemo, useState } from "react";

const DAYS = ["E Hënë", "E Martë", "E Mërkurë", "E Enjte", "E Premte"];
const PERIODS = [
    { p: 1, time: "08:00 – 08:45" },
    { p: 2, time: "08:50 – 09:35" },
    { p: 3, time: "09:40 – 10:25" },
    { p: 4, time: "10:55 – 11:40" },
    { p: 5, time: "11:45 – 12:30" },
    { p: 6, time: "12:35 – 13:20" },
    { p: 7, time: "13:25 – 14:10" },
];
const BREAK_AFTER = { 1: "5'", 2: "5'", 3: "30' (pushim i madh)", 4: "5'", 5: "5'", 6: "5'" };

const COLORS = [
    "#1E3A5F", "#457B9D", "#2A9D8F", "#E76F51", "#E9C46A",
    "#F4A261", "#264653", "#C084FC", "#6366F1", "#EF4444",
];

const colorForSubject = (subjectId) => {
    if (!subjectId) return "#E5E7EB";
    let hash = 0;
    for (let i = 0; i < subjectId.length; i++) hash = (hash * 31 + subjectId.charCodeAt(i)) & 0xffffffff;
    return COLORS[Math.abs(hash) % COLORS.length];
};

/**
 * Grid timetable view.
 * - slots: [{class_id, day, period, subject_id, color}]
 * - subjects: [{id, name}]
 * - editable: when true, clicking a cell opens onCellClick(day, period, currentSlot)
 */
export const TimetableGrid = ({ slots = [], subjects = [], editable = false, onCellClick }) => {
    const subjectMap = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s])), [subjects]);
    const slotMap = useMemo(() => {
        const m = {};
        for (const s of slots) m[`${s.day}-${s.period}`] = s;
        return m;
    }, [slots]);

    return (
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-2xl">
            <table className="w-full min-w-[640px] border-collapse">
                <thead>
                    <tr className="bg-bone/60 text-[10px] uppercase tracking-widest text-slate-700 font-bold">
                        <th className="text-left px-3 py-2 w-28">Ora / Dita</th>
                        {DAYS.map((d) => (
                            <th key={d} className="text-center px-2 py-2">{d}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {PERIODS.map((p) => (
                        <>
                            <tr key={`row-${p.p}`} className="border-t border-slate-200">
                                <td className="px-3 py-2 align-middle">
                                    <div className="text-xs font-bold text-oxford">Ora {p.p}</div>
                                    <div className="text-[10px] text-slate-500 font-mono">{p.time}</div>
                                </td>
                                {DAYS.map((_, dayIdx) => {
                                    const slot = slotMap[`${dayIdx}-${p.p}`];
                                    const subj = slot ? subjectMap[slot.subject_id] : null;
                                    const bg = slot ? (slot.color || colorForSubject(slot.subject_id)) : "#FFFFFF";
                                    const text = slot ? "#FFFFFF" : "#94a3b8";
                                    return (
                                        <td key={`${dayIdx}-${p.p}`} className="p-1 align-stretch">
                                            <button
                                                type="button"
                                                onClick={editable ? () => onCellClick && onCellClick(dayIdx, p.p, slot) : undefined}
                                                disabled={!editable}
                                                className={`w-full h-full min-h-[52px] rounded-md px-2 py-1.5 text-left transition ${editable ? "hover:ring-2 hover:ring-oxford/30 cursor-pointer" : "cursor-default"}`}
                                                style={{ background: bg, color: text }}
                                                data-testid={`tt-cell-${dayIdx}-${p.p}`}
                                            >
                                                {slot && subj ? (
                                                    <div className="text-xs font-bold leading-tight line-clamp-2">{subj.name}</div>
                                                ) : (
                                                    <div className="text-[10px] text-slate-400 italic">{editable ? "+ Shto" : "—"}</div>
                                                )}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                            {p.p in BREAK_AFTER && (
                                <tr key={`brk-${p.p}`} className="bg-bone/30">
                                    <td colSpan={DAYS.length + 1} className="px-3 py-1 text-[10px] text-slate-500 italic text-center">
                                        Pushim {BREAK_AFTER[p.p]}
                                    </td>
                                </tr>
                            )}
                        </>
                    ))}
                </tbody>
            </table>
            <div className="px-4 py-3 bg-bone/40 border-t border-slate-200 text-[11px] text-slate-600 flex flex-wrap gap-3">
                <span><strong>Shën / Diel:</strong> Ditë pushimi</span>
                <span><strong>Mësimi:</strong> 08:00 – 14:10 (7 orë × 45 min)</span>
            </div>
        </div>
    );
};
